import type { App } from 'vue'
import type { Router } from 'vue-router'
import { env } from '@/utils/env'
import {
  isTrackingDisabledForCurrentPath,
  isTrackingDisabledPath,
} from '@/utils/trackingRoute'

let appInstance: App | null = null
let cookieConsentReady: Promise<void> | null = null
let plausibleReady: Promise<void> | null = null
let plausibleInstalled = false
let plausibleRouteHookRegistered = false
let plausibleDevNoticeShown = false

function getAppInstance() {
  return appInstance
}

export function startDeferredClientModules(app: App) {
  if (typeof window === 'undefined') return

  appInstance = app

  const router = app.config.globalProperties.$router as Router | undefined

  if (router && !plausibleRouteHookRegistered) {
    plausibleRouteHookRegistered = true
    router.afterEach((to) => {
      if (!isTrackingDisabledPath(to.path)) {
        void ensurePlausibleReady()
      }
    })
  }

  if (isTrackingDisabledForCurrentPath()) return

  void ensurePlausibleReady()
}

export function ensureCookieConsentReady() {
  if (typeof window === 'undefined') return Promise.resolve()
  if (cookieConsentReady) return cookieConsentReady

  cookieConsentReady = (async () => {
    const app = getAppInstance()
    if (!app) return

    const [
      { default: CookieConsentPlugin },
      { createCookieConsentConfig, syncInitialConsent },
    ] = await Promise.all([
      import('@/plugins/cookie-consent.ts'),
      import('@/config/cookieconsent/config'),
      import('vanilla-cookieconsent/dist/cookieconsent.css'),
      import('../../assets/css/overrides.css'),
    ])

    app.use(CookieConsentPlugin, {
      getConfig: (currentApp: App) => createCookieConsentConfig(currentApp),
    })

    syncInitialConsent()
  })()

  return cookieConsentReady
}

export async function showCookieConsentIfNeeded() {
  if (typeof window === 'undefined') return

  await ensureCookieConsentReady()

  const CookieConsent = await import('vanilla-cookieconsent')

  if (!CookieConsent.validConsent()) {
    CookieConsent.show(true)
  }
}

function ensurePlausibleReady() {
  if (typeof window === 'undefined') return Promise.resolve()
  if (isTrackingDisabledForCurrentPath()) return Promise.resolve()
  if (env.isDev) {
    if (!plausibleDevNoticeShown) {
      console.info('[Plausible disabled in dev]')
      plausibleDevNoticeShown = true
    }

    return Promise.resolve()
  }

  if (plausibleInstalled) return Promise.resolve()
  if (plausibleReady) return plausibleReady

  plausibleReady = (async () => {
    try {
      const app = getAppInstance()
      if (!app) return

      const { plausible } = await import('@/config/plausible-config')

      if (isTrackingDisabledForCurrentPath()) return

      app.use(plausible)
      plausibleInstalled = true
    } finally {
      if (!plausibleInstalled) {
        plausibleReady = null
      }
    }
  })()

  return plausibleReady
}
